/**
 * Guest session helpers (localStorage + payment confirmation snapshot).
 */

export const PAYMENT_CONFIRM_STORAGE_KEY = 'jadecaps_payment_confirm';

export function paymentConfirmLockKey(bookingId, paymentId) {
  return `jadecaps_confirm_${bookingId}_${paymentId}`;
}

export function isPaymentConfirmLocked(bookingId, paymentId) {
  if (!bookingId || !paymentId) return false;
  return sessionStorage.getItem(paymentConfirmLockKey(bookingId, paymentId)) === '1';
}

export function lockPaymentConfirm(bookingId, paymentId) {
  if (!bookingId || !paymentId) return;
  try {
    sessionStorage.setItem(paymentConfirmLockKey(bookingId, paymentId), '1');
  } catch {
    // ignore
  }
}

export function unlockPaymentConfirm(bookingId, paymentId) {
  if (!bookingId || !paymentId) return;
  try {
    sessionStorage.removeItem(paymentConfirmLockKey(bookingId, paymentId));
  } catch {
    // ignore
  }
}

/** Prevent duplicate POST /booking/store for the same property + stay dates in one tab session */
export function bookingCreateLockKey(propertyId, checkinDate, checkoutDate) {
  const pid = propertyId ?? '';
  const inDate = String(checkinDate ?? '').slice(0, 10);
  const outDate = String(checkoutDate ?? '').slice(0, 10);
  return `jadecaps_create_${pid}_${inDate}_${outDate}`;
}

export function isBookingCreateLocked(propertyId, checkinDate, checkoutDate) {
  try {
    return sessionStorage.getItem(bookingCreateLockKey(propertyId, checkinDate, checkoutDate)) === '1';
  } catch {
    return false;
  }
}

export function lockBookingCreate(propertyId, checkinDate, checkoutDate) {
  try {
    sessionStorage.setItem(bookingCreateLockKey(propertyId, checkinDate, checkoutDate), '1');
  } catch {
    // ignore
  }
}

export function unlockBookingCreate(propertyId, checkinDate, checkoutDate) {
  try {
    sessionStorage.removeItem(bookingCreateLockKey(propertyId, checkinDate, checkoutDate));
  } catch {
    // ignore
  }
}

/** After successful confirm-payment — block any second confirm for this booking id */
export function markBookingConfirmedLocally(bookingId) {
  if (!bookingId) return;
  try {
    sessionStorage.setItem(`jadecaps_booking_confirmed_${bookingId}`, '1');
  } catch {
    // ignore
  }
}

export function isBookingConfirmedLocally(bookingId) {
  if (!bookingId) return false;
  try {
    return sessionStorage.getItem(`jadecaps_booking_confirmed_${bookingId}`) === '1';
  } catch {
    return false;
  }
}

const PENDING_CHECKOUT_KEY = 'jadecaps_pending_checkout';

/** Persist lead booking while Razorpay / webhook may finish after refresh. */
export function setPendingCheckout(payload) {
  if (!payload?.bookingId) return;
  try {
    sessionStorage.setItem(
      PENDING_CHECKOUT_KEY,
      JSON.stringify({
        bookingId: Number(payload.bookingId),
        orderId: payload.orderId || null,
        slug: payload.slug || null,
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore
  }
}

export function readPendingCheckout() {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.bookingId) return null;
    // Ignore stale pending rows older than 2 hours
    if (parsed.savedAt && Date.now() - Number(parsed.savedAt) > 2 * 60 * 60 * 1000) {
      clearPendingCheckout();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingCheckout() {
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // ignore
  }
}

export function isBookingConfirmInFlight(bookingId) {
  if (!bookingId) return false;
  try {
    return sessionStorage.getItem(`jadecaps_confirm_inflight_${bookingId}`) === '1';
  } catch {
    return false;
  }
}

export function lockBookingConfirmInFlight(bookingId) {
  if (!bookingId) return;
  try {
    sessionStorage.setItem(`jadecaps_confirm_inflight_${bookingId}`, '1');
  } catch {
    // ignore
  }
}

export function unlockBookingConfirmInFlight(bookingId) {
  if (!bookingId) return;
  try {
    sessionStorage.removeItem(`jadecaps_confirm_inflight_${bookingId}`);
  } catch {
    // ignore
  }
}

export function getCustomerToken() {
  try {
    const token = localStorage.getItem('customerToken');
    if (!token || token === 'null' || token === 'undefined') return null;
    return token;
  } catch {
    return null;
  }
}

export function getCustomerRefreshToken() {
  try {
    return localStorage.getItem('customerRefreshToken') || '';
  } catch {
    return '';
  }
}

export function setCustomerTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem('customerToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('customerRefreshToken', refreshToken);
  }
}

export function persistCustomerSession(data) {
  if (!data || typeof data !== 'object') return data;
  const accessToken = data.accessToken || data.token;
  if (accessToken) localStorage.setItem('customerToken', accessToken);
  if (data.refreshToken) localStorage.setItem('customerRefreshToken', data.refreshToken);

  const profile = { ...data };
  delete profile.token;
  delete profile.accessToken;
  delete profile.refreshToken;
  delete profile.password;
  localStorage.setItem('loggedCustomer', JSON.stringify(profile));
  // The widget reloads its chat list: chats from before login now belong to the account.
  try {
    window.dispatchEvent(new Event('jadecaps:auth-changed'));
  } catch {
    // ignore
  }
  return profile;
}

/**
 * Ask Jade chat: on logout the chats stay with the account (they come back on the next login),
 * and this browser gets a fresh anonymous chat identity so the next visitor can't see them.
 */
function resetJadeChat() {
  try {
    localStorage.removeItem('jadecaps_chat_visitor');
    sessionStorage.removeItem('jadecaps_chat_active');
    sessionStorage.removeItem('jadecaps_chat_guest');
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new Event('jadecaps:auth-changed'));
  } catch {
    // ignore
  }
}

export function clearCustomerAuth() {
  localStorage.removeItem('loggedCustomer');
  localStorage.removeItem('customerToken');
  localStorage.removeItem('customerRefreshToken');
  resetJadeChat();
}

/** Axios config — omit Authorization when there is no real token (avoids "Bearer null" → 401). */
export function customerAuthHeaders() {
  const token = getCustomerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export function extractPaymentCustomer(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const customer = payload.customer ?? payload.data?.customer;
  if (!customer || typeof customer !== 'object') return null;
  const token = customer.token ?? customer.accessToken;
  if (!token) return null;
  return {
    ...customer,
    token,
    accessToken: token,
    refreshToken: customer.refreshToken || null,
  };
}

export function mergePaymentBooking(apiBooking, stateBooking, paymentId) {
  const safeState =
    stateBooking && typeof stateBooking === 'object' ? { ...stateBooking } : {};
  // Create-booking response stores Razorpay `amount` in paise — not a display total.
  if (safeState.orderId != null && safeState.grandTotal == null) {
    delete safeState.amount;
  }
  const merged = {
    ...safeState,
    ...(apiBooking && typeof apiBooking === 'object' ? apiBooking : {}),
  };
  if (paymentId) {
    merged.paymentId = paymentId;
    merged.razorpayPaymentId = paymentId;
    merged.transactionId = paymentId;
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

export function persistPaymentConfirmation(payload, stateBooking = null, savedProperty = null, paymentId = null) {
  try {
    if (!payload && !stateBooking) return;
    const booking = mergePaymentBooking(payload?.booking, stateBooking, paymentId);
    sessionStorage.setItem(
      PAYMENT_CONFIRM_STORAGE_KEY,
      JSON.stringify({
        booking,
        savedProperty: savedProperty && typeof savedProperty === 'object' ? savedProperty : null,
        customer: extractPaymentCustomer(payload),
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

export function readPaymentConfirmation() {
  try {
    const raw = sessionStorage.getItem(PAYMENT_CONFIRM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPaymentConfirmation() {
  try {
    sessionStorage.removeItem(PAYMENT_CONFIRM_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Clear guest session from localStorage and reload app state.
 * @param {string} [redirectTo='/'] - Path to open after logout (use '' to reload current page).
 */
export function customerLogout(redirectTo = '/') {
  const refreshToken = getCustomerRefreshToken();
  const { REACT_APP_API_URL } = process.env;
  if (refreshToken && REACT_APP_API_URL) {
    const token = getCustomerToken();
    fetch(`${REACT_APP_API_URL}/customer/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {});
  }
  clearCustomerAuth();
  clearPaymentConfirmation();
  if (redirectTo) {
    window.location.href = redirectTo;
  } else {
    window.location.reload();
  }
}

export function isCustomerLoggedIn() {
  return Boolean(getCustomerToken() && localStorage.getItem('loggedCustomer'));
}
